import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { AddProductsToGroupModalComponent } from "@components/add-products-to-group-modal/add-products-to-group-modal.component";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { DiscardGroupModalComponent } from "@components/discard-group-modal/discard-group-modal.component";
import { GroupConfigurationTableComponent } from "@components/group-configuration-table/group-configuration-table.component";
import { GroupIdChipComponent } from "@components/group-number-chip/group-id-chip.component";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import type { ScopedProject } from "@domain/project/project-guards";
import { ContentBoxComponent } from "@odx/angular/components/content-box";
import { ModalService, ModalSize } from "@odx/angular/components/modal";
import { TableModule } from "@odx/angular/components/table";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";

@Component({
  selector: "app-group-configuration",
  standalone: true,
  imports: [
    ConfigurationWizardBackButtonComponent,
    TableModule,
    ContentBoxComponent,
    UiKitModule,
    ReactiveFormsModule,
    GroupConfigurationTableComponent,
    GroupIdChipComponent,
  ],
  templateUrl: "./group-configuration.component.html",
  styleUrl: "./group-configuration.component.scss",
})
export class GroupConfigurationComponent implements OnInit {
  private activeRoute = inject(ActivatedRoute);
  private formBuilder = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  @ViewChild("groupTable")
  private groupConfigurationTable!: GroupConfigurationTableComponent;

  protected selectedProducts: ProductTypesInGroup[] = [];

  protected project!: ScopedProject;
  protected floorplan!: Floorplan;

  protected readonly tooltipTextRemoveProducts: string = $localize`:@@groupConfiguration.bar.button.removeProducts:Produkte aus Gruppe entfernen`;

  protected groupNameForm!: FormGroup;

  protected group!: ProductItemGroup;

  protected productsToDisplay!: ProductTypesInGroup[];

  private readonly modalService = inject(ModalService);

  constructor() {
    this.project = this.activeRoute.parent!.snapshot.data["project"];
    this.floorplan = this.project.getFloorplanById(this.activeRoute.parent!.snapshot.queryParams["floorplan_id"])!;
    const groupId = this.activeRoute.snapshot.queryParams["group_id"];
    this.group = this.project.groups.find((g) => g.id === groupId)!;
    this.productsToDisplay = this.resolveProductsFromReferences(this.group);
  }

  ngOnInit() {
    this.initForm();
  }

  private resolveProductsFromReferences(group: ProductItemGroup): ProductTypesInGroup[] {
    return group.references.map((ref) => this.resolveReference(ref)).filter((p): p is ProductTypesInGroup => p !== null);
  }

  private resolveReference(ref: FloorplanItemReference): ProductTypesInGroup | null {
    const floorplan = this.project.getFloorplanById(ref.floorplanId);
    if (!floorplan) return null;

    const allItems: ProductTypesInGroup[] = [
      ...floorplan.alarmDevices,
      ...floorplan.alarmDevicePlaceholders,
      ...floorplan.gasWarningCenters,
      ...floorplan.gasWarningCenterPlaceholders,
      ...floorplan.transmitters,
      ...floorplan.transmitterPlaceholders,
      ...floorplan.signalElements,
      ...floorplan.signalElementPlaceholders,
      ...floorplan.plasticSigns,
      ...floorplan.plasticSignPlaceholders,
      ...floorplan.patientMonitors,
      ...floorplan.patientMonitorPlaceholders,
      ...floorplan.centralStations,
      ...floorplan.centralStationPlaceholders,
      ...floorplan.telemetries,
      ...floorplan.telemetryPlaceholders,
      ...floorplan.kvms,
      ...floorplan.kvmPlaceholders,
      ...floorplan.displays,
      ...floorplan.displayPlaceholders,
      ...floorplan.utilities,
      ...floorplan.utilityPlaceholders,
      ...floorplan.flameDetectors,
      ...floorplan.flameDetectorPlaceholders,
    ];

    return allItems.find((item) => item.id === ref.floorplanProductItemId) ?? null;
  }

  private initForm() {
    this.groupNameForm = this.formBuilder.group({
      name: [this.group?.name ?? "", [Validators.required, Validators.maxLength(50)]],
    });

    this.groupNameForm.valueChanges.subscribe((value) => {
      if (!this.groupNameForm.valid || this.groupNameForm.pristine) return;
      this.group.name = value.name;
    });
  }

  protected readonly FloorplanMenuType = FloorplanMenuType;

  addProducts() {
    this.modalService
      .open(AddProductsToGroupModalComponent, {
        data: {
          group: this.group,
          floorplan: this.floorplan,
          project: this.project,
          allGroups: this.project.groups,
        },
        size: ModalSize.LARGE,
      })
      .onClose$.subscribe((addedReferences: FloorplanItemReference[]) => {
        const newProducts = addedReferences
          .map((ref) => this.resolveReference(ref))
          .filter((p): p is ProductTypesInGroup => p !== null);
        this.productsToDisplay = [...this.productsToDisplay, ...newProducts];
      });
  }

  deselect() {
    this.groupConfigurationTable.deselectAll();
  }

  removeProductsFromGroup() {
    this.selectedProducts.forEach((product) => {
      const ref = this.group.references.find((r) => r.floorplanProductItemId === product.id);
      if (ref) {
        this.group.removeReference(ref.floorplanId, product.id);
      }
    });
    this.project.removeGroupIfEmpty(this.group);
    this.productsToDisplay = this.productsToDisplay.filter(
      (product) => !this.selectedProducts.some((selected) => selected.id === product.id),
    );
    this.selectedProducts = [];
  }

  openDeleteGroupModal() {
    this.modalService
      .open(ConfirmModalComponent, {
        data: {
          text: $localize`:@@modal.deleteGroup.askForConfirmation:Soll die Gruppe wirklich aufgelöst werden? Die sich darin befindlichen Produkte werden nicht gelöscht.`,
        },
      })
      .onClose$.subscribe(() => {
        this.deleteGroup();
      });
  }

  protected onSelectedProductsChanged($event: ProductTypesInGroup[]) {
    this.selectedProducts = $event;
  }

  private deleteGroup() {
    this.project.removeGroup(this.group.id);
    this.floorplan.publishUpdate(FloorplanEventType.GROUP_DELETED, this.group);
    this.floorplan.floorplanState.selectedGroupId = undefined;
    this.navigate();
  }

  navigateBack() {
    if (this.productsToDisplay.length <= 0) {
      this.modalService.open(DiscardGroupModalComponent).onClose$.subscribe(() => {
        this.floorplan.floorplanState.selectedGroupId = undefined;
        this.deleteGroup();
      });
    } else {
      this.floorplan.floorplanState.selectedGroupId = undefined;
      this.navigate();
    }
  }

  navigate() {
    const floorplanId = this.activatedRoute.snapshot.queryParams["floorplan_id"];

    this.router.navigate(["/projects", this.project.id, "floorplans", floorplanId], {
      queryParams: {
        selectedGroup: FloorplanMenuType.GROUP,
      },
    });
  }
}
